import AnimationManager from './AnimationManager.js';
import { current, isDraft } from 'immer';
import { EffectType } from './constants.js';
import audioManager from '../utils/AudioManager.js';

/**
 * プレゼンテーション制御システム
 * ゲームメカニクスと視覚表現を分離し、演出を管理する
 */
class PresentationController {
    constructor() {
        // 演出キュー
        this.animationQueue = [];
        
        // 現在実行中の演出
        this.currentAnimation = null;
        
        // ゲームメカニクス一時停止フラグ
        this.gameLogicPaused = false;
        
        // ゲームメカニクス再開コールバック
        this.resumeGameLogicCallback = null;
        
        // 視覚状態
        this.visualState = {
            cards: new Map(),
            players: new Map()
        };
        
        // AnimationManagerを統合
        this.animationManager = new AnimationManager();
    }

    /**
     * GameStateへの参照を設定
     */
    setGameState(gameState) {
        const processedGameState = isDraft(gameState) ? current(gameState) : gameState;
        this.gameState = processedGameState; 
        if (this.animationManager) {
            this.animationManager.setGameState(processedGameState);
        }
    }

    /**
     * ゲームメカニクスを一時停止
     */
    pauseGameLogic(resumeCallback) {
        this.gameLogicPaused = true;
        this.resumeGameLogicCallback = resumeCallback;
    }

    /**
     * ゲームメカニクスを再開
     */
    resumeGameLogic() {
        if (this.gameLogicPaused && this.resumeGameLogicCallback) {
            this.gameLogicPaused = false;
            const callback = this.resumeGameLogicCallback;
            this.resumeGameLogicCallback = null;
            callback();
        }
    }

    /**
     * 演出をキューに追加
     */
    enqueueAnimation(animation) {
        this.animationQueue.push({
            ...animation,
            id: Date.now() + Math.random(),
            timestamp: Date.now()
        });
        this.processAnimationQueue();
    }

    /**
     * 演出キューを処理
     */
    async processAnimationQueue() {
        if (this.currentAnimation) return;
        if (this.animationQueue.length === 0) {
            if (this.gameLogicPaused) this.resumeGameLogic();
            return;
        }

        const nextAnimation = this.animationQueue.shift();
        this.currentAnimation = nextAnimation;
        
        try {
            await this.executeAnimation(nextAnimation);
        } catch (error) {
            console.error('🎭 [Presentation] Animation execution error:', error);
        } finally {
            this.currentAnimation = null;
            setTimeout(() => this.processAnimationQueue(), 50);
        }
    }

    /**
     * 演出を実行（旧来のシステムとの互換性）
     */
    async executeAnimation(animation) {
        switch (animation.type) {
            case 'CARD_DAMAGE':
                await this.executeCardDamageAnimation(animation);
                break;
            case 'CARD_DESTROY':
                await this.executeCardDestroyAnimation(animation);
                break;
            case 'CARD_MOVE':
                await this.executeCardMoveAnimation(animation);
                break;
            case 'RESOURCE_CHANGE':
                await this.executeResourceChangeAnimation(animation);
                break;
            default:
                console.warn('🎭 [Presentation] Unknown animation type:', animation.type);
                break;
        }
    }

    async executeCardDamageAnimation(animation) {
        const { cardId } = animation.data;
        const element = document.querySelector(`[data-card-id="${cardId}"]`);
        if (!element) {
            console.warn('🎭 [Presentation] Card element not found for damage animation:', cardId);
            return;
        }
        return new Promise((resolve) => {
            element.classList.add('card-damage-animation');
            setTimeout(() => {
                element.classList.remove('card-damage-animation');
                resolve();
            }, 800);
        });
    }

    async executeCardDestroyAnimation(animation) {
        const { cardId } = animation.data;
        const element = document.querySelector(`[data-card-id="${cardId}"]`);
        if (!element) {
            console.warn('🎭 [Presentation] Card element not found for destroy animation:', cardId);
            return;
        }
        return new Promise((resolve) => {
            element.classList.add('card-destroy-animation');
            setTimeout(() => {
                element.classList.remove('card-destroy-animation');
                element.style.display = 'none';
                resolve();
            }, 600);
        });
    }

    async executeCardMoveAnimation() {
        return new Promise((resolve) => setTimeout(resolve, 300));
    }

    async executeResourceChangeAnimation() {
        return new Promise((resolve) => setTimeout(resolve, 800));
    }

    /**
     * ゲームエフェクトを監視して演出を生成
     */
    async onGameEffect(gameState, effect, sourceCard) {
        if (!effect || !effect.effect_type) return;

        // ソースカードのフォールバック検索
        if (!sourceCard && effect.args?.card_id && this.gameState) {
            sourceCard = this.gameState.all_card_instances[effect.args.card_id];
        }

        this.setGameState(gameState);
        await this.delegateToAnimationManager(effect, sourceCard);
    }

    /**
     * AnimationManagerに演出を委譲
     */
    async delegateToAnimationManager(effect, sourceCard) {
        if (!this.animationManager) {
            console.warn('🎮GAME_ANIM [Presentation] AnimationManager not available');
            return;
        }

        try {
            switch (effect.effect_type) {
                case 'MOVE_CARD':
                    await this.handleCardMove(effect, sourceCard);
                    break;
                case 'DAMAGE_THIS':
                case 'BOOST_THIS':
                    await this.handleCardDurabilityChange(effect, sourceCard);
                    break;
                case 'MODIFY_CARD_DURABILITY':
                    // 内部処理用のためスキップ
                    break;
                case 'WEALTH_DURABILITY_ZERO_THIS':
                case 'CARD_DESTROYED':
                    await this.handleCardDestroy(effect, sourceCard);
                    break;
                case 'CONSCIOUSNESS_CHANGED':
                case 'SCALE_CHANGED':
                case 'MODIFY_CONSCIOUSNESS_RESERVE':
                case 'MODIFY_SCALE_RESERVE':
                    await this.handleResourceChange(effect, sourceCard);
                    break;
                case 'TURN_START':
                    await this.handleTurnStart(effect, sourceCard);
                    break;
                case 'TURN_END':
                    await this.handleTurnEnd(effect, sourceCard);
                    break;
                case 'MATCH_END_DETAIL':
                    await this.handleMatchEndDetail(effect, sourceCard);
                    break;
                case 'GAME_RESULT':
                    await this.handleGameResult(effect, sourceCard);
                    break;
                case 'TURN_ORDER_DECISION':
                    await this.handleTurnOrderDecision(effect, sourceCard);
                    break;
                case 'LIMIT_WARNING':
                    await this.handleLimitWarning(effect, sourceCard);
                    break;
                case 'EFFECT_NULLIFIED':
                    await this.handleEffectNullified(effect, sourceCard);
                    break;
                case 'EVENT_CARD_PLAYED':
                    await this.handleEventCardPlayed(effect, sourceCard);
                    break;
                case 'CARD_REVEALED':
                    await this.handleCardRevealed(effect, sourceCard);
                    break;
                default:
                    // console.log('🎭 [Presentation] No animation mapping for effect:', effect.effect_type);
                    break;
            }
        } catch (error) {
            console.error('🎭 [Presentation] Error delegating to AnimationManager:', error);
        }

        // 規模等に影響を与えるエフェクト後に継続演出を更新
        const scaleAffectingEffects = [
            EffectType.MOVE_CARD, EffectType.CARD_DURABILITY_CHANGED, EffectType.SCALE_CHANGED,
            EffectType.MODIFY_SCALE_RESERVE, EffectType.TURN_START, EffectType.WEALTH_DURABILITY_ZERO_THIS,
            EffectType.CARD_DESTROYED, EffectType.DRAW_CARD, EffectType._PLACE_IDEOLOGY,
            EffectType.SET_SCALE, EffectType.MODIFY_SCALE, EffectType.REMOVE_CARD_FROM_GAME
        ];

        if (scaleAffectingEffects.includes(effect.effect_type)) {
            if (this.animationManager) {
                setTimeout(() => this.animationManager.updatePersistentAnimations(this.gameState), 50);
            }
        }
    }

    async handleCardPlacement(effect, sourceCard) {
        const cardId = effect.args.card_id;
        const target = document.querySelector(`[data-card-id="${cardId}"]`);
        if (target) {
            audioManager.playSE('draw'); // 配置開始時はドロー音
            await this.animationManager.triggerTransientEffect('CARD_PLAY', target, { effect, sourceCard });
        } else {
            console.warn('🔥ANIM_DEBUG [Presentation] Card element not found for placement:', cardId);
        }
    }

    async handleEventCardPlayed(effect, sourceCard) {
        audioManager.playSE('draw'); // イベントプレイ開始時はドロー音
        await this.animationManager.triggerTransientEffect('EVENT_CARD_PLAYED', null, { effect, sourceCard });
    }

    async handleCardRevealed(effect, sourceCard) {
        await this.animationManager.triggerTransientEffect('CARD_REVEALED', null, { effect, sourceCard });
    }

    async handleCardMove(effect, sourceCard) {
        const cardId = effect.args.card_id;
        const sourcePile = effect.args.source_pile;
        const destPile = effect.args.destination_pile;
        
        // ドロー演出（デッキから手札）
        if (sourcePile === 'deck' && destPile === 'hand') {
            audioManager.playSE('draw');
            setTimeout(async () => {
                const target = document.querySelector(`[data-card-id="${cardId}"]`);
                await this.animationManager.triggerTransientEffect('CARD_DRAW', target, { effect, sourceCard });
            }, 50);
        }
        // 手札から場への移動（配置演出）
        else if (sourcePile === 'hand' && (destPile === 'field' || destPile === 'ideology')) {
            audioManager.playSE('draw'); // プレイ開始時はドロー音
            this.hideCardImmediately(cardId);
            setTimeout(async () => {
                const target = document.querySelector(`[data-card-id="${cardId}"]`);
                if (target) {
                    await this.animationManager.triggerTransientEffect('CARD_PLAY', target, { effect, sourceCard, delay: 500 });
                }
            }, 50);
        }
        // 生成（ゲーム外から場へ）
        else if (sourcePile === 'game_source' && destPile === 'field') {
            const cardInstance = this.gameState?.all_card_instances?.[cardId];
            const isIdeology = cardInstance?.card_type === 3; // CardType.IDEOLOGY
            audioManager.playSE(isIdeology ? 'ideology_place' : 'place');
            this.hideCardImmediately(cardId);
            setTimeout(async () => {
                const target = document.querySelector(`[data-card-id="${cardId}"]`);
                if (target) {
                    await this.animationManager.triggerTransientEffect('CARD_PLAY', target, { effect, sourceCard, delay: 500 });
                }
            }, 50);
        }

        // 演出不要な移動（手札から事象再生エリア等）
        else if (sourcePile === 'hand' && destPile === 'playing_event') {
            // スキップ
        }
    }

    async handleCardDurabilityChange(effect, sourceCard) {
        try {
            const cardId = effect.args.damaged_card_id || effect.args.boosted_card_id;
            const target = document.querySelector(`[data-card-id="${cardId}"]`);
            const changeAmount = effect.args.damage_amount || effect.args.boost_amount || 0;
            
            if (target) {
                if (changeAmount < 0) audioManager.playSE('damage');
                else if (changeAmount > 0) audioManager.playSE('heal');
                
                await this.animationManager.triggerTransientEffect('CARD_DAMAGE', target, { 
                    effect: { ...effect, args: { ...effect.args, amount: changeAmount } }, 
                    sourceCard, 
                    delay: 500 
                });
                
                if (this.gameLogicPaused) this.resumeGameLogic();
            } else {
                console.warn('🔥ANIM_DEBUG [Presentation] Card element not found for durability change:', cardId);
            }
        } catch (error) {
            console.error(error);
        }
    }
    
    async handleCardDestroy(effect, sourceCard) {
        const cardId = effect.args.card_id;
        const target = document.querySelector(`[data-card-id="${cardId}"]`);
        if (target) {
            await this.animationManager.triggerTransientEffect('CARD_DESTROY', target, { effect, sourceCard, delay: 500 });
        } else {
            console.warn('🎭 [Presentation] Card element not found for destroy:', cardId);
        }
    }

    async handleResourceChange(effect, sourceCard) {
        const playerId = effect.args.player_id;
        const resourceType = effect.effect_type.includes('CONSCIOUSNESS') ? 'consciousness' : 'scale';
        const playerElement = document.querySelector(`[data-player-id="${playerId}"]`);
        
        if (playerElement) {
            // 堅牢なセレクター群
            const resourceSelectors = [
                `.${resourceType}`, 
                `.player-${resourceType}`, 
                `[data-resource="${resourceType}"]`,
                `.resource-${resourceType}`,
                `.stat-${resourceType}`
            ];
            
            let resourceElement = null;
            for (const selector of resourceSelectors) {
                resourceElement = playerElement.querySelector(selector);
                if (resourceElement) break;
            }
            
            if (resourceElement) {
                const effectType = effect.effect_type.includes('CONSCIOUSNESS') ? 'CONSCIOUSNESS_CHANGE_RESULT' : 'SCALE_CHANGE_RESULT';
                await this.animationManager.triggerTransientEffect(effectType, resourceElement, { effect, sourceCard });
            } else {
                console.warn('🔥ANIM_DEBUG [Presentation] Resource element not found with any selector');
            }
        }
    }

    /**
     * カードを即座に隠す（DOM更新前）
     */
    hideCardImmediately(cardId) {
        const elements = document.querySelectorAll(`[data-card-id="${cardId}"]`);
        elements.forEach(element => {
            element.style.setProperty('visibility', 'hidden', 'important');
            element.style.setProperty('opacity', '0', 'important');
            element.style.setProperty('transform', 'scale(0)', 'important');
            element.classList.add('card-animation-hidden');
        });
        this.addHidingCSSRule(cardId);
    }

    /**
     * カード隠蔽用のCSSルールを動的に追加
     */
    addHidingCSSRule(cardId) {
        const styleId = `hide-${cardId}`;
        if (document.getElementById(styleId)) document.getElementById(styleId).remove();
        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = `
            [data-card-id="${cardId}"]:not(.card-animation-clone) { 
                visibility: hidden !important; 
                opacity: 0 !important; 
                transform: scale(0) !important;
            }
        `;
        document.head.appendChild(style);
    }

    async handleTurnStart(effect, sourceCard) {
        audioManager.playSE('turn_start');
        if (this.animationManager) {
            await this.animationManager.triggerTransientEffect('TURN_START', null, { 
                playerName: effect.args.player_name || 'PLAYER', 
                turnNumber: effect.args.turn_number || 1,
                delay: 750
            });
        }
    }

    async handleTurnEnd(effect, sourceCard) {
        if (this.animationManager) {
            await this.animationManager.triggerTransientEffect('TURN_END', null, { 
                playerName: effect.args.player_name || 'PLAYER',
                delay: 750
            });
        }
    }

    async handleMatchEndDetail(effect, sourceCard) {
        if (this.animationManager) {
            await this.animationManager.triggerTransientEffect('MATCH_END_DETAIL', null, { ...effect.args });
        }
    }

    async handleGameResult(effect, sourceCard) {
        const isVictory = effect.args.is_victory || false;
        audioManager.stopBGM();

        if (this.animationManager) {
            await this.animationManager.triggerTransientEffect('GAME_RESULT', null, { isVictory, message: effect.args.message || '' });
        }
    }

    async handleLimitWarning(effect, sourceCard) {
        const { player_id, limit_type } = effect.args;
        let targetSelector = limit_type === 'hand' 
            ? (player_id === 'PLAYER1' ? '.player-hand-area' : '.opponent-hand-area')
            : (player_id === 'PLAYER1' ? '.player-field-area' : '.opponent-field-area');
        
        const target = document.querySelector(targetSelector);
        if (target && this.animationManager) {
            await this.animationManager.triggerTransientEffect('LIMIT_WARNING', target, { limitType: limit_type, message: effect.args.message });
        }
    }

    async handleEffectNullified(effect, sourceCard) {
        const target = document.querySelector(`[data-card-id="${effect.args.target_card_id}"]`);
        if (target && this.animationManager) {
            await this.animationManager.triggerTransientEffect('EFFECT_NULLIFIED', target, {});
        }
    }

    async handleTurnOrderDecision(effect, sourceCard) {
        if (this.animationManager) {
            await this.animationManager.triggerTransientEffect('TURN_ORDER_DECISION', null, { 
                firstPlayer: effect.args.first_player || 'PLAYER 1', 
                secondPlayer: effect.args.second_player || 'PLAYER 2',
                delay: 1000
            });
        }
    }

    getStatus() {
        return {
            queueLength: this.animationQueue.length,
            currentAnimation: this.currentAnimation?.type || null,
            gameLogicPaused: this.gameLogicPaused
        };
    }

    clear() {
        this.animationQueue = [];
        this.currentAnimation = null;
        if (this.gameLogicPaused) this.resumeGameLogic();
    }
}

export default PresentationController;
